/**
 * X (Twitter) Bookmarks integration API client
 */

import { supabase } from '@/lib/supabase';
import { apiService } from './api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export type XBookmarkSyncInterval = 5 | 15 | 60;

export type XBookmarkBackfillStatus =
  | 'pending'
  | 'in_progress'
  | 'complete'
  | 'failed'
  | 'limit_reached';

export interface XBookmarksStatus {
  connected: boolean;
  x_username?: string | null;
  x_user_id?: string | null;
  sync_interval_minutes: XBookmarkSyncInterval;
  backfill_status: XBookmarkBackfillStatus;
  last_sync_at?: string | null;
  bookmarks_imported_count: number;
  last_error?: string | null;
  next_sync_at?: string | null;
}

export interface XBookmarksSyncResult {
  success: boolean;
  fetched?: number;
  created?: number;
  skipped?: number;
  limit_reached?: boolean;
  mode?: string;
  error?: string;
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Memara-Client': 'ui',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const xBookmarksService = {
  async prepareBinding(
    bindingId: string,
    syncIntervalMinutes: XBookmarkSyncInterval
  ): Promise<{ sync_interval_minutes: number }> {
    const payload = await apiService.post<{ success: boolean; data: { sync_interval_minutes: number } }>(
      `/api/v1/integrations/x-bookmarks/${bindingId}/prepare`,
      { sync_interval_minutes: syncIntervalMinutes }
    );
    return payload.data;
  },

  async getConnectUrl(bindingId: string, redirectAfter?: string): Promise<string> {
    const payload = await apiService.get<{ success: boolean; data: { authorize_url: string } }>(
      `/api/v1/integrations/x-bookmarks/${bindingId}/connect-url`,
      redirectAfter ? { redirect_after: redirectAfter } : undefined
    );
    return payload.data.authorize_url;
  },

  async getStatus(bindingId: string): Promise<XBookmarksStatus> {
    const payload = await apiService.get<{ success: boolean; data: XBookmarksStatus }>(
      `/api/v1/integrations/x-bookmarks/${bindingId}/status`
    );
    return payload.data;
  },

  async updateSettings(
    bindingId: string,
    syncIntervalMinutes: XBookmarkSyncInterval
  ): Promise<{ sync_interval_minutes: number }> {
    const payload = await patchJson<{ success: boolean; data: { sync_interval_minutes: number } }>(
      `/api/v1/integrations/x-bookmarks/${bindingId}/settings`,
      { sync_interval_minutes: syncIntervalMinutes }
    );
    return payload.data;
  },

  async syncNow(bindingId: string): Promise<XBookmarksSyncResult> {
    const payload = await apiService.post<{ success: boolean; data: XBookmarksSyncResult }>(
      `/api/v1/integrations/x-bookmarks/${bindingId}/sync`
    );
    return payload.data;
  },

  async disconnect(bindingId: string): Promise<void> {
    await apiService.delete(`/api/v1/integrations/x-bookmarks/${bindingId}/disconnect`);
  },
};
